/**
 * useGlobalAudioAnalyser Hook
 * 
 * Captures real-time audio data from the global Fr00t audio system.
 * Connects an analyser node to the main master gain to visualize all audio output.
 */

import { useState, useEffect, useRef } from 'react';
import { audioManager } from '../core/audio/AudioManagerAdapter';
import { perfTrail } from '../core/PerformanceTrail';

interface GlobalAudioData {
  frequencyData: Uint8Array;
  averageAmplitude: number;
}

export function useGlobalAudioAnalyser() {
  const [audioData, setAudioData] = useState<GlobalAudioData | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const analyseVersionRef = useRef<number>(0); // Version counter — kills stale rAF callbacks

  useEffect(() => {
    let mounted = true;
    let unsubscribeFn: (() => void) | null = null;

    const setupAnalyser = async () => {
      // Wait for AudioManager to be ready
      if (!audioManager.isReady()) {
        unsubscribeFn = audioManager.onInitialized(() => {
          if (mounted) {
            setupAnalyser();
          }
        });
        return;
      }

      const audioContext = audioManager.getMainAudioContext();
      const masterGain = audioManager.getMainMasterGain();

      if (!audioContext || !masterGain) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Global audio analyser: Audio context or master gain not available');
        }
        return;
      }

      // Create analyser node
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024; // 512 bins — sufficient for cymatic visualization
      analyser.smoothingTimeConstant = 0.4; // Quick response — reaches 63% in ~120ms at 30fps
      analyserRef.current = analyser;

      // Connect analyser to master gain output
      // In Web Audio API, you can connect multiple nodes from one source
      // This won't break the existing connection to destination
      masterGain.connect(analyser);

      // Start analysis loop with aggressive frame throttling for performance
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      let frameCount = 0;
      const targetFPS = 30;
      const frameInterval = 1000 / targetFPS;
      let lastUpdateTime = 0;
      const stateUpdateThrottle = 6; // Update React state ~5×/sec for responsive hologram
      let lastIsPlaying = false;
      let lastAmplitude = 0;

      const analyze = () => {
        // Bail if unmounted or a newer mount superseded this loop
        if (!mounted || analyseVersionRef.current !== analyseVersion || !analyserRef.current) {
          return;
        }

        perfTrail.start('audio-buffer-update');
        const now = performance.now();
        analyser.getByteFrequencyData(dataArray);

        // Calculate average amplitude for overall visualization intensity
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const averageAmplitude = sum / bufferLength;

        // Check if audio is playing (has significant amplitude)
        const hasAudio = averageAmplitude > 2;

        // Throttle React state updates to ~2.5 FPS — only when meaningful change detected
        frameCount++;
        if (mounted && (now - lastUpdateTime >= frameInterval) && (frameCount % stateUpdateThrottle === 0)) {
          lastUpdateTime = now;
          // Only dispatch when amplitude changed >3% or playing state toggled
          const ampChanged = Math.abs(averageAmplitude - lastAmplitude) > (lastAmplitude * 0.03 + 0.3);
          if (ampChanged || hasAudio !== lastIsPlaying) {
            lastAmplitude = averageAmplitude;
            lastIsPlaying = hasAudio;
            setAudioData({
              frequencyData: new Uint8Array(dataArray),
              averageAmplitude
            });
            setIsPlaying(hasAudio);
          }
        }

        perfTrail.end('audio-buffer-update');
        animationFrameRef.current = requestAnimationFrame(analyze);
      };

      analyze();
    };

    // Increment version and capture it BEFORE setupAnalyser so the analyze closure sees the correct version
    analyseVersionRef.current += 1;
    const analyseVersion = analyseVersionRef.current;
    setupAnalyser();

    return () => {
      mounted = false;
      analyseVersionRef.current += 1; // Atomically kill all in-flight rAF callbacks
      // Unsubscribe from audio manager if subscription exists
      if (unsubscribeFn) {
        unsubscribeFn();
        unsubscribeFn = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
    };
  }, []);

  return { audioData, isPlaying };
}

